import {
  Component,
  Input,
  OnInit,
  ViewChild,
  computed,
  signal
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { NotaFiscalEletronica } from "@interfaces/nfe";
import { Option } from "@interfaces/option";
import { ModalService } from "@services/modal.service";
import { NfeService } from "@services/nfe.service";
import { MenuItem } from "primeng/api";
import { Subscription } from "rxjs";
import { OverlayPanel } from "primeng/overlaypanel";
import {
  openInCurrentBrowserContext,
  openInExternalBrowser,
  openDownloadPopupIfNeeded,
  preOpenExternalDownloadTab,
  saveBlobFile,
  shouldUseExternalDownloadLink
} from "src/app/utils/file-download.util";

type NfeTipoImpressao = "1" | "2" | "3";

@Component({
  selector: "app-nfe-card",
  templateUrl: "./nfe-card.component.html",
  styleUrls: ["./nfe-card.component.scss"]
})
export class NfeCardComponent implements OnInit {
  @Input() nfe?: NotaFiscalEletronica;
  @ViewChild("overlay") overlay!: OverlayPanel;
  $: Subscription[] = [];

  items = signal<MenuItem[]>([]);

  private readonly tipoImpressaoOptions: Option<NfeTipoImpressao>[] = [
    { display: "DANFE normal - Retrato", value: "1" },
    { display: "DANFE normal - Paisagem", value: "2" },
    { display: "DANFE simplificado", value: "3" }
  ];

  get canSync() {
    return this.nfe?.status === "PROCESSANDO";
  }

  get canDownloadFiles() {
    return this.nfe?.status === "CANCELADO" || this.nfe?.status === "CONCLUIDO";
  }

  get canDownloadXml() {
    return this.canDownloadFiles || this.nfe?.status === "REJEITADO";
  }

  get rejected() {
    return this.nfe?.status === "REJEITADO";
  }

  menuOptions = computed(() => [
    {
      items: this.items()
    }
  ]);

  constructor(
    private readonly nfeService: NfeService,
    private readonly modalService: ModalService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    if (this.canDownloadXml) {
      this.addOption(
        "Baixar XML",
        "pi-download",
        this.download.bind(this, "xml")
      );
    }

    if (this.canDownloadFiles) {
      this.addOption(
        "Baixar PDF",
        "pi-file-pdf",
        this.download.bind(this, "pdf")
      );

      this.addOption("Solicitar correção", "pi-pencil", () => {
        this.modalService.input(
          "Solicitar correção",
          "Informe o motivo pelo qual você deseja corrigir a nota",
          (value) => {
            this.editNfe(value ?? "");
          },
          undefined,
          {
            fieldName: "correcao",
            label: "Correção",
            required: true,
            type: "textarea"
          }
        );
      });

      this.addOption("Solicitar cancelamento", "pi-times", () => {
        this.modalService.deletion(
          "Solicitar cancelamento",
          "Informe o motivo pelo qual você deseja cancelar a nota",
          (value) => {
            const justificativa = (value ?? "").trim();
            if (justificativa.length < 15) {
              this.modalService.error(
                "A justificativa deve conter no mínimo 15 caracteres."
              );
              return;
            }
            this.cancelNfe(justificativa);
          },
          undefined,
          {
            fieldName: "justificativa",
            label: "Justificativa",
            required: true,
            type: "textarea",
            hint: "Mínimo de 15 caracteres.",
            invalidMessage:
              "A justificativa deve conter no mínimo 15 caracteres.",
            isValid: (value) => (value?.trim().length ?? 0) >= 15
          }
        );
      });
    }

    if (this.rejected) {
      this.addOption("Validar XML", "pi-external-link", () => {
        window.open(
          "https://validador.nfe.tecnospeed.com.br/",
          "_blank",
          "noopener,noreferrer"
        );
      });
    }

    if (this.nfe?.mv?.payload) {
      this.addOption("Duplicar nota", "pi-copy", () => {
        this.modalService.confirm(
          "Você será redirecionado para a tela de emissão de notas fiscais para duplicar a nota. Deseja continuar?",
          () => {
            this.router.navigate(["/nfe"], {
              queryParams: {
                payload: this.nfe?.mv?.payload
              }
            });
          }
        );
      });
    }
  }

  cancelNfe(justificativa: string) {
    this.$.push(
      this.nfeService
        .requestNfeCancellation(this.nfe?.id ?? "", justificativa)
        .subscribe({
          next: () => {
            this.modalService.success(
              "Solicitação de cancelamento enviada com sucesso!"
            );
          },
          error: (err) => {
            this.modalService.error(
              this.extractErrorMessage(
                err,
                "justificativa",
                "Ocorreu um erro ao solicitar o cancelamento da nota"
              )
            );
          }
        })
    );
  }

  editNfe(correcao: string) {
    if (correcao.length < 15 || correcao.length > 255) {
      this.modalService.error("A correção deve ter entre 15 e 255 caracteres");
      return;
    }
    this.$.push(
      this.nfeService
        .requestNfeCorrection(this.nfe?.id ?? "", correcao)
        .subscribe({
          next: () => {
            this.modalService.success(
              "Solicitação de correção enviada com sucesso!"
            );
          },
          error: (err) => {
            this.modalService.error(
              this.extractErrorMessage(
                err,
                "correcao",
                "Ocorreu um erro ao solicitar a correção da nota"
              )
            );
          }
        })
    );
  }

  private extractErrorMessage(
    err: HttpErrorResponse,
    field: "justificativa" | "correcao",
    fallback: string
  ): string {
    return (
      err?.error?.error?.data?.fields?.[field] ||
      err?.error?.response?.fields?.[field] ||
      err?.error?.error?.message ||
      err?.error?.message ||
      err?.message ||
      fallback
    );
  }

  public download(type: "xml" | "pdf"): void {
    const nfeId = this.nfe?.id ?? "";

    if (shouldUseExternalDownloadLink()) {
      if (type === "pdf") {
        this.downloadPdfWithLegacy404Fallback(nfeId);
        return;
      }

      this.openExternalDownloadLink(nfeId, type);
      return;
    }

    const popup = openDownloadPopupIfNeeded();

    this.$.push(
      this.nfeService.downloadNfeFile(nfeId, type).subscribe({
        next: (res) => {
          const rawFilename = `${this.nfe?.destinatario}-S${this.nfe?.serie}-N${this.nfe?.numero}.${type}`;
          const filename = this.sanitizeFilename(rawFilename);

          if (type === "pdf") {
            void this.handlePdfBlobDownloadResponse(res, filename, popup);
            return;
          }

          saveBlobFile(res, filename, popup);
          this.modalService.success(
            "Download iniciado. Verifique os arquivos do seu dispositivo."
          );
        },
        error: (err: HttpErrorResponse) => {
          if (type === "pdf" && this.isDownloadNotFound(err)) {
            this.promptNfePdfReprint(nfeId, popup);
            return;
          }

          popup?.close();
          this.modalService.error("Não foi possível baixar o arquivo da nota.");
        }
      })
    );
  }

  private downloadPdfWithLegacy404Fallback(nfeId: string): void {
    const externalTab = preOpenExternalDownloadTab();

    this.$.push(
      this.nfeService.downloadNfeFile(nfeId, "pdf").subscribe({
        next: () => {
          this.openExternalDownloadLink(nfeId, "pdf", externalTab);
        },
        error: (err: HttpErrorResponse) => {
          if (this.isDownloadNotFound(err)) {
            externalTab?.close();
            this.promptNfePdfReprint(nfeId);
            return;
          }

          externalTab?.close();
          this.modalService.error("Não foi possível baixar o arquivo da nota.");
        }
      })
    );
  }

  private openExternalDownloadLink(
    nfeId: string,
    type: "xml" | "pdf",
    externalTab: Window | null = preOpenExternalDownloadTab()
  ): void {
    this.$.push(
      this.nfeService.getExternalNfeDownloadLink(nfeId, type).subscribe({
        next: ({ url }) => {
          const opened = openInExternalBrowser(url, externalTab);

          if (opened) {
            this.modalService.success(
              "Download aberto no navegador externo do dispositivo."
            );
            return;
          }

          this.handleExternalOpenFailure(url);
        },
        error: () => {
          externalTab?.close();
          this.modalService.error(
            "Não foi possível preparar o link externo de download."
          );
        }
      })
    );
  }

  private isDownloadNotFound(err: HttpErrorResponse): boolean {
    return err.status === 404 || err.error?.statusCode === 404;
  }

  private promptNfePdfReprint(nfeId: string, popup?: Window | null): void {
    this.modalService.inputs(
      "Reimpressão de NF-e",
      "PDF não encontrado para download direto. Informe os parâmetros de reimpressão.",
      (values) => {
        const formValues = (values ?? {}) as {
          tipoImpressao?: string;
        };

        const tipoImpressao = (formValues.tipoImpressao ?? "").trim();

        if (!tipoImpressao) {
          popup?.close();
          return;
        }

        this.downloadNfePdfReprint(
          nfeId,
          {
            tipoImpressao,
            modeloImpressao: tipoImpressao
          },
          popup
        );
      },
      () => {
        popup?.close();
      },
      [
        {
          fieldName: "tipoImpressao",
          label: "Tipo de impressão",
          required: true,
          type: "select",
          options: this.tipoImpressaoOptions
        }
      ]
    );
  }

  private downloadNfePdfReprint(
    nfeId: string,
    payload: {
      tipoImpressao?: string;
      modeloImpressao?: string;
    },
    popup?: Window | null
  ): void {
    this.$.push(
      this.nfeService.reprintNfePdf(nfeId, payload).subscribe({
        next: (res) => {
          const rawFilename = `${this.nfe?.destinatario}-S${this.nfe?.serie}-N${this.nfe?.numero}.pdf`;
          const filename = this.sanitizeFilename(rawFilename);

          void this.handlePdfBlobDownloadResponse(res, filename, popup);
        },
        error: (err: HttpErrorResponse) => {
          this.handleNfePdfReprintError(err, popup);
        }
      })
    );
  }

  private handleNfePdfReprintError(
    err: HttpErrorResponse,
    popup?: Window | null
  ): void {
    popup?.close();

    if (err.error instanceof Blob) {
      err.error
        .text()
        .then((text) => {
          const message =
            this.extractErrorMessageFromRawText(text) ||
            this.extractHttpErrorMessage(
              err,
              "Não foi possível reimprimir o PDF da nota."
            );

          this.modalService.error(message);
        })
        .catch(() => {
          this.modalService.error(
            this.extractHttpErrorMessage(
              err,
              "Não foi possível reimprimir o PDF da nota."
            )
          );
        });

      return;
    }

    this.modalService.error(
      this.extractHttpErrorMessage(
        err,
        "Não foi possível reimprimir o PDF da nota."
      )
    );
  }

  private async handlePdfBlobDownloadResponse(
    blob: Blob,
    filename: string,
    popup?: Window | null
  ): Promise<void> {
    const isPdf = await this.isPdfBlob(blob);

    if (isPdf) {
      saveBlobFile(blob, filename, popup);
      this.modalService.success(
        "Download iniciado. Verifique os arquivos do seu dispositivo."
      );
      return;
    }

    popup?.close();

    const rawText = await blob.text().catch(() => "");

    const message =
      this.extractErrorMessageFromRawText(rawText) ||
      "O PDF ainda está sendo gerado. Tente novamente em alguns instantes.";

    if (this.isProcessingMessage(rawText)) {
      this.modalService.info(message, "PDF em processamento");
      return;
    }

    this.modalService.error(message);
  }

  private async isPdfBlob(blob: Blob): Promise<boolean> {
    const normalizedType = (blob.type ?? "").toLowerCase();

    if (normalizedType.includes("application/pdf")) {
      return true;
    }

    try {
      const sample = await blob.slice(0, 1024).arrayBuffer();
      const signature = new TextDecoder("ascii")
        .decode(new Uint8Array(sample))
        .toUpperCase();

      return signature.includes("%PDF-");
    } catch {
      return false;
    }
  }

  private isProcessingMessage(rawText: string): boolean {
    const normalized = rawText.toLowerCase();

    return (
      normalized.includes("processando") ||
      normalized.includes("ainda está sendo gerado") ||
      normalized.includes("ainda esta sendo gerado")
    );
  }

  private extractErrorMessageFromRawText(text: string): string | undefined {
    const trimmedText = text.trim();

    if (!trimmedText) {
      return undefined;
    }

    try {
      const parsedError = JSON.parse(trimmedText) as {
        error?: { message?: string };
        message?: string;
      };

      return parsedError?.error?.message || parsedError?.message || trimmedText;
    } catch {
      return trimmedText;
    }
  }

  private extractHttpErrorMessage(
    err: HttpErrorResponse,
    fallback: string
  ): string {
    return (
      err?.error?.error?.message ||
      err?.error?.message ||
      err?.message ||
      fallback
    );
  }

  private handleExternalOpenFailure(url: string) {
    this.modalService.info(
      "Não foi possível abrir automaticamente. Toque em confirmar para abrir o download neste navegador.",
      "Abrir download",
      () => {
        const openedHere = openInCurrentBrowserContext(url);

        if (openedHere) {
          return;
        }

        navigator.clipboard
          .writeText(url)
          .then(() => {
            this.modalService.success(
              "Link copiado. Cole no navegador externo para concluir o download."
            );
          })
          .catch(() => {
            this.modalService.error(
              "Não foi possível abrir neste navegador nem copiar o link automaticamente."
            );
          });
      }
    );
  }

  public addOption(label: string, icon: string, command: () => void) {
    this.items.update((old) => [
      ...old,
      {
        label,
        icon: `pi ${icon}`,
        command
      }
    ]);
  }

  public showError() {
    if (this.nfe?.status !== "REJEITADO") {
      return this.modalService.info(
        "Não é possível visualizar o erro de uma nota que não foi rejeitada.",
        "Ação não permitida"
      );
    }

    this.modalService.info(this.nfe?.mensagem, "Motivo da rejeição");
  }

  copyToClipboard(id: string | undefined) {
    if (!id) {
      return;
    }
    const textToCopy = `ID: ${id} `;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        this.modalService.success("Copiado para a área de transferência!");
      })
      .catch(() => {
        this.modalService.error("Falha ao copiar!");
      });
  }

  private sanitizeFilename(filename: string): string {
    return filename
      .normalize("NFD")
      .replaceAll(/[\u0300-\u036f]/g, "")
      .replaceAll(/[^a-zA-Z0-9._-]/g, "_")
      .replaceAll(/_+/g, "_")
      .trim();
  }

  showOverlay(event: Event) {
    this.overlay.toggle(event);
  }
}
